import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Search, Calendar, MapPin, Users } from 'lucide-react';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import HotelFilterSidebar from '../components/HotelFilterSidebar';
import HotelCard from '../components/HotelCard';
import CompactRoomSelector from '../components/CompactRoomSelector';
import Footer from '../components/Footer';

export default function ResultsPage({ 
  searchData: propSearchData, 
  onSelectHotel, 
  hotels: propHotels = [], 
  cities = [], 
  onSearch 
}) {

  // ✅ LOCAL STATE with fallback to props
  const [searchData, setSearchData] = useState(propSearchData);
  const [hotels, setHotels] = useState(propHotels);
  const [isRestoring, setIsRestoring] = useState(false);

  // ✅ RESTORE from localStorage if props are null (page reload)
  useEffect(() => {
  if (propSearchData) {
    setSearchData(propSearchData);
  }
  if (propHotels && propHotels.length > 0) {
    setHotels(propHotels);
  }
}, [propSearchData, propHotels]);

// ✅ Safety check: if STILL no data after mount, redirect
useEffect(() => {
  // Give it a moment to hydrate
  const timer = setTimeout(() => {
    if (!searchData || !hotels || hotels.length === 0) {
      window.location.href = '/';
    }
  }, 100);
  
  return () => clearTimeout(timer);
}, []);


  const [filters, setFilters] = useState({
    name: '',
    stars: [],
    pensions: []
  });
  // const [currentPage, setCurrentPage] = useState(1);
  // const hotelsPerPage = 4;

  // ✅ Editable search state
  const [editDestination, setEditDestination] = useState(searchData?.destinationId || '');
  const [editDateRange, setEditDateRange] = useState([{
    startDate: searchData?.checkIn ? parseDate(searchData.checkIn) : new Date(),
    endDate: searchData?.checkOut ? parseDate(searchData.checkOut) : new Date(new Date().setDate(new Date().getDate() + 3)),
    key: 'selection'
  }]);
  const [editRooms, setEditRooms] = useState(searchData?.rooms || [{ adults: 2, children: [] }]);
  const [showCalendar, setShowCalendar] = useState(false);
  
  const calendarRef = useRef(null);

  const [showSearchBar, setShowSearchBar] = useState(true);

  // Parse date from "DD/MM/YYYY" format
  function parseDate(dateStr) {
    if (dateStr instanceof Date) return dateStr;
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      return new Date(parts[2], parts[1] - 1, parts[0]);
    }
    return new Date();
  }

  // ✅ Update editDestination when searchData changes
  useEffect(() => {
    if (searchData?.destinationId) {
      setEditDestination(searchData.destinationId);
    }
    if (searchData?.checkIn && searchData?.checkOut) {
      setEditDateRange([{
        startDate: parseDate(searchData.checkIn),
        endDate: parseDate(searchData.checkOut),
        key: 'selection'
      }]);
    }
    if (searchData?.rooms) {
      setEditRooms(searchData.rooms);
    }
  }, [searchData]);

  // Close calendar on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  // Handle new search
  const handleNewSearch = () => {
    if (!editDestination) {
      alert('Veuillez sélectionner une destination');
      return;
    }
    
    onSearch({
      destination: editDestination,
      checkIn: editDateRange[0].startDate,
      checkOut: editDateRange[0].endDate,
      rooms: editRooms
    });
  };

  const formatDate = (date) => {
    if (!date) return '';
    if (typeof date === 'string') return date;
    if (date instanceof Date) {
      return date.toLocaleDateString('fr-FR');
    }
    return '';
  };

  const availableFilters = useMemo(() => {
    const stars = new Set();
    const pensions = new Set();
    hotels.forEach(hotel => {
      if (hotel.stars) stars.add(hotel.stars);
      if (hotel.pensions && Array.isArray(hotel.pensions)) {
        hotel.pensions.forEach(pension => {
          if (pension.type) pensions.add(pension.type);
        });
      }
    });
    return {
      stars: Array.from(stars).sort((a, b) => a - b),
      pensions: Array.from(pensions)
    };
  }, [hotels]);

  const filteredHotels = useMemo(() => {
    return hotels.filter(hotel => {
      if (filters.name && !hotel.name.toLowerCase().includes(filters.name.toLowerCase())) {
        return false;
      }
      if (filters.stars.length > 0 && !filters.stars.includes(hotel.stars)) {
        return false;
      }
      if (filters.pensions.length > 0) {
        const hasMatchingPension = hotel.pensions?.some(p => filters.pensions.includes(p.type));
        if (!hasMatchingPension) return false;
      }
      return true;
    });
  }, [filters, hotels]);

  // const totalPages = Math.ceil(filteredHotels.length / hotelsPerPage);
  // const indexOfLastHotel = currentPage * hotelsPerPage;
  // const indexOfFirstHotel = indexOfLastHotel - hotelsPerPage;
  // const currentHotels = filteredHotels.slice(indexOfFirstHotel, indexOfLastHotel);

  // useMemo(() => {
  //   setCurrentPage(1);
  // }, [filters]);

  // const handlePageChange = (pageNumber) => {
  //   setCurrentPage(pageNumber);
  //   window.scrollTo({ top: 0, behavior: 'smooth' });
  // };

  // ✅ SHOW LOADING while restoring
  if (isRestoring || !searchData || !hotels) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF7B47]"></div>
          <p className="mt-4 text-gray-600 font-semibold">Restauration des résultats...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="flex-grow">
        <div className="container mx-auto px-4 py-8">

          {/* ✅ Toggle Button */}
          <button
            onClick={() => setShowSearchBar(!showSearchBar)}
            className="mb-4 w-full bg-white hover:bg-gray-50 border-2 border-[#FF7B47] rounded-lg px-4 py-3 flex items-center justify-between transition shadow-md"
          >
            <span className="font-semibold text-[#2B4979] flex items-center gap-2">
              <Search size={20} className="text-[#FF7B47]" />
              {showSearchBar ? 'Masquer la recherche' : 'Modifier la recherche'}
            </span>
            {showSearchBar ? (
              <ChevronUp size={20} className="text-[#FF7B47]" />
            ) : (
              <ChevronDown size={20} className="text-[#FF7B47]" />
            )}
          </button>

          {/* ✅ Collapsible Search Bar */}
          {showSearchBar && (
            <div className="bg-white shadow-md sticky top-16 z-30 border-2 border-gray-200 rounded-lg mb-6 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Search size={18} className="text-[#FF7B47]" />
                <h3 className="font-bold text-[#2B4979]">Modifier votre recherche</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {/* Destination */}
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                    <MapPin size={12} className="text-[#FF7B47]" />
                    Destination
                  </label>
                  <select
                    value={editDestination}
                    onChange={(e) => setEditDestination(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-[#FF7B47] focus:outline-none text-sm"
                  >
                    <option value="">Choisir...</option>
                    {cities.map((city) => (
                      <option key={city.Id} value={city.Id}>
                        {city.Name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Dates */}
                <div className="relative" ref={calendarRef}>
                  <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                    <Calendar size={12} className="text-[#FF7B47]" />
                    Dates
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowCalendar(!showCalendar)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg hover:border-[#FF7B47] transition text-left bg-white text-sm"
                  >
                    {editDateRange[0].startDate.toLocaleDateString('fr-FR')} - {editDateRange[0].endDate.toLocaleDateString('fr-FR')}
                  </button>

                  {showCalendar && (
                    <div className="absolute top-full left-0 mt-2 z-50 bg-white rounded-lg shadow-2xl border-2 border-gray-200">
                      <DateRange
                        ranges={editDateRange}
                        onChange={(item) => setEditDateRange([item.selection])}
                        months={2}
                        direction="horizontal"
                        minDate={new Date()}
                        rangeColors={['#FF7B47']}
                      />
                      <div className="p-4 border-t">
                        <button
                          type="button"
                          onClick={() => setShowCalendar(false)}
                          className="w-full bg-[#2B4979] text-white py-2 rounded-lg hover:bg-[#1e3454] transition text-sm"
                        >
                          Confirmer
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Rooms */}
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                    <Users size={12} className="text-[#FF7B47]" />
                    Voyageurs
                  </label>
                  <CompactRoomSelector rooms={editRooms} setRooms={setEditRooms} />
                </div>

                {/* Search Button */}
                <div className="flex items-end">
                  <button
                    onClick={handleNewSearch}
                    className="w-full bg-[#FF7B47] hover:bg-[#ff6a30] text-white px-4 py-2 rounded-lg transition font-semibold text-sm flex items-center justify-center gap-2"
                  >
                    <Search size={16} />
                    Rechercher
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Search Summary */}
          <div className="mb-6 bg-white rounded-lg shadow-sm p-4 border-l-4 border-[#FF7B47]">
            <h1 className="text-2xl font-bold text-[#2B4979] mb-2">
              Résultats de recherche
            </h1>
            <p className="text-gray-600 flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-[#FF7B47]">{searchData?.destination}</span>
              <span>•</span>
              <span>{formatDate(searchData?.checkIn)} - {formatDate(searchData?.checkOut)}</span>
              <span>•</span>
              <span>{searchData?.rooms?.length || 0} chambre(s)</span>
              <span>•</span>
              <span className="font-semibold text-[#2B4979]">{filteredHotels.length} hôtel(s) trouvé(s)</span>
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar Filters */}
            <div className="lg:col-span-1">
              <HotelFilterSidebar 
                filters={filters} 
                setFilters={setFilters}
                availableStars={availableFilters.stars}
                availablePensions={availableFilters.pensions}
                hotels={hotels}
              />
            </div>

            {/* Hotel Results */}
            <div className="lg:col-span-3">
              {filteredHotels.length > 0 ? (
                <>
                  <div className="space-y-6">
                    {filteredHotels.map(hotel => (
                      <HotelCard 
                        key={hotel.id} 
                        hotel={hotel} 
                        onSelectHotel={onSelectHotel}
                        searchData={searchData} 
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {/* {totalPages > 1 && (
                    <div className="mt-8 flex items-center justify-center gap-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold ${
                          currentPage === 1
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-[#2B4979] hover:bg-[#FF7B47] hover:text-white shadow-md'
                        }`}
                      >
                        <ChevronLeft size={20} />
                        Précédent
                      </button>

                      <div className="flex gap-2">
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          const shouldShow = 
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);
                          const showEllipsisBefore = pageNumber === currentPage - 2 && currentPage > 3;
                          const showEllipsisAfter = pageNumber === currentPage + 2 && currentPage < totalPages - 2;

                          if (showEllipsisBefore || showEllipsisAfter) {
                            return <span key={pageNumber} className="px-3 py-2 text-gray-400">...</span>;
                          }
                          if (!shouldShow) return null;

                          return (
                            <button
                              key={pageNumber}
                              onClick={() => handlePageChange(pageNumber)}
                              className={`w-10 h-10 rounded-lg transition font-semibold ${
                                currentPage === pageNumber
                                  ? 'bg-[#FF7B47] text-white shadow-md'
                                  : 'bg-white text-[#2B4979] hover:bg-gray-100 shadow-sm'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-semibold ${
                          currentPage === totalPages
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-[#2B4979] hover:bg-[#FF7B47] hover:text-white shadow-md'
                        }`}
                      >
                        Suivant
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  )} */}

{/* <div className="mt-4 text-center text-sm text-gray-600">
  Affichage de {indexOfFirstHotel + 1}-{Math.min(indexOfLastHotel, filteredHotels.length)} sur {filteredHotels.length} hôtels
</div> */}
                </>
              ) : (
                <div className="bg-white rounded-xl shadow-md p-12 text-center">
                  <div className="text-6xl mb-4">🏨</div>
                  <h3 className="text-2xl font-bold text-gray-700 mb-2">
                    Aucun hôtel trouvé
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Essayez d'ajuster vos filtres pour voir plus de résultats
                  </p>
                  <button
                    onClick={() => setFilters({ name: '', stars: [], pensions: [] })}
                    className="bg-[#FF7B47] hover:bg-[#ff6a30] text-white px-6 py-3 rounded-lg transition font-semibold"
                  >
                    Réinitialiser les filtres
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
